/**
 * ContainerMoreInfoPage Component
 *
 * Summary:
 * This component represents the detailed information page for a specific containerData. It allows users to view and manage various aspects of a container, including its devices, crops, sensor thresholds, and graphical data.
 *
 * Key functionalities:
 * - Fetches and displays container image and device data.
 * - Allows users to move devices within the container's floor plan.
 * - Fetches and displays crops associated with the containerData.
 * - Fetches and updates sensor thresholds for the containerData.
 * - Provides a graphical view of sensor data over time.
 * - Utilizes the toast notification library to display notifications.
 * - Dynamically adjusts the layout based on the screen width.
 * - Implements various data fetching functions for different views.
 * - Handles user interactions and updates data accordingly.
 * - Includes a helper modal to guide users on the page functionalities.
 */

import React, { useContext, useState, useEffect, useRef } from "react";
import UserContext from "../../../context/UserContext";
import { useNavigate, useParams } from "react-router-dom";
import {
  getAllSitesByCompanyId,
  getAllContainersBySiteId,
  getAllContainerDataByContainerId,
  getContainerImageByContainerId,
  getAllDeviceDataByContainerId,
  getAllCropsByContainerId,
  getCompanyByContainerId,
} from "../../../api/locationApi";
import { getAllCompanies } from "../../../api/settingsCompanyApi";
import {
  getAllSensorsByContainerId,
  updateEverySensorThresholdsByContainerId,
  updateEverySensorThresholdsAdditiveByContainerId,
  getAllSensorsWithNoMesDataByContainerId,
  updateSensorThresholdsBySensorIdInWarnings,
} from "../../../api/sensorApi";
import {
  addNewCrop,
  connectCropToContainer,
  deleteCropFromContainer,
  getAllCropsByCompanyId,
} from "../../../api/cropApi";
import { useScreenWidth } from "../../../hooks/useScreenWidth";
import ContainerFloorPlanView from "./ContainerFloorPlanView/ContainerFloorPlanView";
import ContainerCropView from "./ContainerCropView/ContainerCropView";
import ContainerThresholdsView from "./ContainerThresholdsView/ContainerThresholdsView";
import { Button, ButtonGroup, FormSelect } from "react-bootstrap";
import { toast } from "react-toastify";
import "./ContainerMoreInfoPage.scss";
import ContainerGraphView from "./ContainerGraphView/ContainerGraphView";
import ConfirmationModal from "../../Shared/ConfirmationModal/ConfirmationModal";
import useTranslation from "../../../hooks/useTranslation";
import PageHeader from "../../Shared/PageHeader/PageHeader";
import SENSOR_CONFIG from "../../../config/sensorConfig";
import USER_ROLES from "../../../constants/userRoles";
import LoadingAnimation from "../../Shared/LoadingAnimation/LoadingAnimation";
import { naturalSort } from "../../../utils/stringUtils";
import useProtectedResource from "../../../hooks/useProtectedResource";
import useNavigateBack from "../../../hooks/useNavigateBack";
import ContainerThreeDView from "./ContainerThreeDView/ContainerThreeDView";

const ContainerMoreInfoPage = () => {
  const { user } = useContext(UserContext);
  const { t } = useTranslation("locationsPage");
  const { t: cropT } = useTranslation("cropsPage");
  const navigate = useNavigate();
  const { containerId } = useParams();
  const imageRef = useRef();
  const [containerData, setContainerData] = useState(null);
  // Get smart back navigation
  const navigateBack = useNavigateBack("/locations");

  //  ---- STATES ----
  // HEADER FILTER states
  // List of companies for super admin and list of sites and containers in the company for super admin and user
  const [listOfCompaniesForSuperAdmin, setListOfCompaniesForSuperAdmin] =
    useState([]);
  const [listOfSitesInTheCompany, setListOfSitesInTheCompany] = useState([]);
  const [listOfContainersInTheSite, setListOfContainersInTheSite] = useState(
    [],
  );
  // -- Selected company for superadmin and site, container for super admin and user
  const [selectedCompanyForSuperAdmin, setSelectedCompanyForSuperAdmin] =
    useState(null);
  const [selectedSiteFromList, setSelectedSiteFromList] = useState(null);
  const [selectedContainerFromList, setSelectedContainerFromList] =
    useState(containerData);
  // -- MAP VIEW STATES
  // Image data for the container floor plan
  const [imageData, setImageData] = useState(null);
  const [imageSize, setImageSize] = useState({ width: 0, height: 0 });
  const [showImageUpload, setShowImageUpload] = useState(false);

  // Device data for the container floor plan
  const [devices, setDevices] = useState([]);
  // -- SENSOR THRESHOLD VIEW STATES
  // Sensor data for the sensor threshold view
  const [
    sensorsWithThresholdsInContainer,
    setSensorsWithThresholdsInContainer,
  ] = useState([]);
  // -- GRAPH VIEW STATES
  // Selected sensors for the graph view
  const [selectedSensors, setSelectedSensors] = useState({});
  const [sensorsForSelectionInGraphView, setSensorsForSelectionInGraphView] =
    useState([]);
  const [graphData, setGraphData] = useState([]);
  const [isLoadingGraphData, setIsLoadingGraphData] = useState(false);

  // -- CROP VIEW STATES
  // Crop data for the crop view
  const [crops, setCrops] = useState([]);
  const [listOfCropsInCompany, setListOfCropsInCompany] = useState([]);
  const [showConnectCropToContainerModal, setShowConnectCropToContainerModal] =
    useState(false);
  const [
    selectedCropToConnectToContainer,
    setSelectedCropToConnectToContainer,
  ] = useState(null);
  const [
    selectedCropToDeleteFromContainer,
    setSelectedCropToDeleteFromContainer,
  ] = useState(null);
  const [showCropDeleteConfirmationModal, setShowCropDeleteConfirmationModal] =
    useState(false);

  // View state
  const [view, setView] = useState("map");

  // Constants
  const screenWidth = useScreenWidth();
  const thresholdViewTableHeader = {
    sens_num: t("depth"),
    sens_timestamp: t("last_meas_time"),
    sens_value: t("measuredValue") + " [°C]",
    min_value: t("minValue"),
    max_value: t("maxValue"),
  };
  const sensorMinThreshold = SENSOR_CONFIG.temperature.MIN_THRESHOLD_CONFIG;
  const sensorMaxThreshold = SENSOR_CONFIG.temperature.MAX_THRESHOLD_CONFIG;

  const siteName = containerData?.sitename || "";
//  ? listOfSitesInTheCompany.find(s => s._id === containerData.sites_id)?.sitename || ""
//  : "";

  const companyName = user.roles === USER_ROLES.SUPER_ADMIN
  ? containerData?.companyname || ""
  : "";

  const pageTitle = [companyName, siteName, containerData?.containername].filter(Boolean).join(" - ");
  // ---------------- CURRENT CONTAINER INFORMATION FUNCTION ----------------
  // Replace direct API call with protected resource hook
  const {
    data: containerDataResponse,
    isLoading: isLoadingContainer,
    error: containerError,
    UnauthorizedModal,
  } = useProtectedResource(
    getAllContainerDataByContainerId,
    containerId,
    "container",
    "/locations",
    navigateBack,
  );

  // Update state when containerDataResponse changes
  useEffect(() => {
    if (containerDataResponse && containerDataResponse[0]) {
      setContainerData(containerDataResponse[0]);
    }
  }, [containerDataResponse]);

  // Fetch container data by container id
  /*   const fetchContainerData = async (containerId) => {
    try {
      const containerData = await getAllContainerDataByContainerId(containerId);
      setContainerData(containerData[0]);
    } catch (error) {
      console.error("Error fetching container data: ", error);
    }
  };

  useEffect(() => {
    fetchContainerData(containerId);
  }, [containerId]);
 */
  // ---------------- HEADER FILTER FUNCTIONS  ----------------
  // Fetch companies for super admin
  const fetchCompaniesForSuperAdmin = async () => {
    try {
      const companies = await getAllCompanies();
      // Sort companies alphabetically, with "Unicomp Kft." at the top
      const sortedCompanies = companies.sort((a, b) => {
        if (a.companyname === "Unicomp Kft.") return -1;
        if (b.companyname === "Unicomp Kft.") return 1;
        return a.companyname.localeCompare(b.companyname);
      });

      setListOfCompaniesForSuperAdmin(sortedCompanies);
    } catch (error) {
      console.error("Error fetching companies: ", error);
    }
  };

  // Fetch sites in the company
  const fetchSitesInCompany = async (companyId) => {
    try {
      const sites = await getAllSitesByCompanyId(companyId);
      if (Array.isArray(sites.data)) {
        const sortedSites = sites.data.sort((a, b) =>
          naturalSort(a.sitename, b.sitename),
        );
        setListOfSitesInTheCompany(sortedSites);
      } else {
        console.error("sites.data is not an array:", sites.data);
      }
    } catch (error) {
      console.error("Error fetching sites: ", error);
    }
  };

  // Fetch containers in the company
  const fetchContainersInSite = async (siteId) => {
    try {
      const containers = await getAllContainersBySiteId(siteId);
      const sortedContainers = containers.data.sort((a, b) =>
        naturalSort(a.containername, b.containername),
      );
      setListOfContainersInTheSite(sortedContainers);
    } catch (error) {
      console.error("Error fetching containers: ", error);
    }
  };

  useEffect(() => {
    if (user.roles === USER_ROLES.SUPER_ADMIN) {
      fetchCompaniesForSuperAdmin();
    } else {
      fetchSitesInCompany(parseInt(user.company_id, 10));
    }
  }, [user]);

  useEffect(() => {
    if (user.roles === USER_ROLES.SUPER_ADMIN && selectedCompanyForSuperAdmin) {
      fetchSitesInCompany(parseInt(selectedCompanyForSuperAdmin));
    }
  }, [selectedCompanyForSuperAdmin]);

  useEffect(() => {
    if (selectedSiteFromList) {
      fetchContainersInSite(selectedSiteFromList);
    }
  }, [selectedSiteFromList]);

  // Company select functions for super admin
  const handleCompanySelectChange = (companyId) => {
    if (!companyId) {
      setSelectedCompanyForSuperAdmin(null);
      setListOfSitesInTheCompany([]);
      setSelectedSiteFromList(null);
      setListOfContainersInTheSite([]);
      setSelectedContainerFromList("");
      return;
    }
    setSelectedCompanyForSuperAdmin(companyId);
    fetchSitesInCompany(companyId);
    setSelectedSiteFromList(null); // Reset site selection
    setListOfContainersInTheSite([]); // Reset container list
    setSelectedContainerFromList(""); // Reset container selection
  };

  // Site select functions
  const handleSiteSelectChange = (siteId) => {
    if (!siteId) {
      setSelectedSiteFromList(null);
      setListOfContainersInTheSite([]);
      setSelectedContainerFromList("");
      return;
    }

    setSelectedSiteFromList(siteId);
    // Fetch containers in the site
    fetchContainersInSite(siteId);
    setSelectedContainerFromList("");
  };

  const handleContainerSelectChange = (containerId) => {
    if (!containerId) {
      setSelectedContainerFromList("");
      return;
    }

    setSelectedContainerFromList(containerId);
    navigate(`/locations/${containerId}`);
  };
  // ---------------- DEVICE MAP VIEW FUNCTIONS ----------------
  // Fetch container image and device data
  useEffect(() => {
    if (containerData && containerData._id) {
      const fetchData = async () => {
        try {
          const containerImage = await getContainerImageByContainerId(
            containerData._id,
          );
          if (containerImage && containerImage.data) {
            setImageData(containerImage.data[0]);
          } else {
            console.error(
              "No container image found for id:",
              containerData._id,
            );
          }

          const devicesData = await getAllDeviceDataByContainerId(
            containerData._id,
          );

          let nextDefaultXPosition = 0; // Reset next default position
          let firstDevice = true; // Flag to track the first device

          const DEFAULT_MARGIN = screenWidth < 576 ? 10 : 5; // Set default margin based on screen width

          const formattedDevices = devicesData.map((device) => {
            let x;
            let y = device.coord_y || 0;

            // Calculate x position based on the default margin
            if (device.coord_x === undefined || device.coord_x === null) {
              if (firstDevice) {
                firstDevice = false;
                x = 0; // Start from the left for the first device
              } else {
                x = nextDefaultXPosition;
              }
              nextDefaultXPosition += DEFAULT_MARGIN; // Update default position for the next device
            } else {
              x = device.coord_x;
            }

            return {
              ...device,
              x,
              y,
            };
          });

          setDevices(formattedDevices);
        } catch (error) {
          console.error("Error fetching data: ", error);
        }
      };

      fetchData();
    }
  }, [containerData]);

  // Update device position when moved
  const handleDeviceMove = (id, percentX, percentY) => {
    // Update device position with percentage-based coordinates
    setDevices((prevDevices) =>
      prevDevices.map((device) =>
        device.probeid === id
          ? { ...device, coord_x: percentX, coord_y: percentY }
          : device,
      ),
    );
  };

  //  ---------------- CROP VIEW FUNCTIONS ----------------

  const fetchCrops = async () => {
    try {
      const cropsInContainer = await getAllCropsByContainerId(
        containerData._id,
      );
      setCrops(cropsInContainer);
      const userCompanyId = parseInt(user.companyId);
      const cropsInCompany = await getAllCropsByCompanyId(userCompanyId);
      setListOfCropsInCompany(cropsInCompany);
    } catch (error) {
      console.error("ContainerMoreInfoPage: Error fetching crops: ", error);
    }
  };

  useEffect(() => {
    if (containerData && containerData._id) {
      fetchCrops();
    }
  }, [containerData]);

  // Handle add new crop to the container button click
  const handleAddNewCropButtonClick = async (newCrop) => {
    if (!newCrop) {
      toast.error(t("crop_data_missing"));
      return;
    }

    console.log("newCrop: ", newCrop);

    const { cropName, description, status } = newCrop;

    if (!cropName || cropName === "") {
      toast.error(t("crop_name_missing"));
      return;
    }

    const cropData = {
      cropName,
      description,
      status,
    };
    try {
      // Check if the user is a super admin or company admin
      if (user.roles === USER_ROLES.SUPER_ADMIN) {
        // If the user is a super admin, if a company is selected, use that company id, otherwise fetch the companyId from the current location
        const selectedCompanyId = parseInt(selectedCompanyForSuperAdmin);
        let companyId;

        if (selectedCompanyId) {
          companyId = selectedCompanyId;
        } else {
          const companyResponse = await getCompanyByContainerId(containerId);
          companyId = parseInt(companyResponse.companyid); // Extract companyid from the first object in the array
        }

        await addNewCrop(companyId, cropData);
      } else {
        // If the user is not a super admin, use the companyId from the user object
        const companyId = parseInt(user.companyId);
        await addNewCrop(companyId, cropData);
      }

      fetchCrops();
      toast.success(t("addNewCropSuccess"));
    } catch (error) {
      if (error.message === "Crop already exists with the given name") {
        toast.error(cropT("cropExistsError"));
      } else {
        console.error("Error adding new crop: ", error);
        toast.error(error.message);
      }
    }
  };
  // Handle connect crop to container button click
  const handleSubmitConnectCropToContainerButtonClick = async (
    containerId,
    cropId,
  ) => {
    try {
      if (!containerId || !cropId || cropId === null || cropId === "") {
        toast.error(t("must_select_crop"));
        return;
      }

      const response = await connectCropToContainer(containerId, cropId);
      if (response) {
        fetchCrops();
        toast.success(t("crop_connection_to_container_success"));
        setSelectedCropToConnectToContainer(null);
        handleCloseConnectCropToContainerModal();
      }
    } catch (error) {
      console.error("Error connecting crop to container: ", error);
      toast.error(t("crop_connection_to_container_error"));
    }
  };

  // Handle show connect crop to container modal
  const handleShowConnectCropToContainerModal = () => {
    setSelectedCropToConnectToContainer(null);
    setShowConnectCropToContainerModal(true);
  };

  // Handle close connect crop to container modal
  const handleCloseConnectCropToContainerModal = () => {
    setSelectedCropToConnectToContainer(null);
    setShowConnectCropToContainerModal(false);
  };

  // Handle show crop delete confirmation modal
  const handleShowCropDeleteConfirmationModal = (cropId) => {
    setSelectedCropToDeleteFromContainer(cropId);
    setShowCropDeleteConfirmationModal(true);
  };

  // Handle delete crop from container
  const handleDeleteCropFromContainerConfirm = async () => {
    if (!selectedCropToDeleteFromContainer) {
      toast.error(t("crop_removal_not_selected"));
      return;
    }

    try {
      const response = await deleteCropFromContainer(
        containerData._id,
        selectedCropToDeleteFromContainer,
      );
      if (response) {
        fetchCrops();
        toast.success(t("crop_removal_from_container_success"));
        setSelectedCropToDeleteFromContainer(null);
        setShowCropDeleteConfirmationModal(false);
      }
    } catch (error) {
      console.error("Error deleting crop from container: ", error);
      toast.error(t("crop_removal_from_container_error"));
    }
  };

  // Handle close crop delete confirmation modal
  const handleDeleteCropFromContainerCancel = () => {
    setSelectedCropToDeleteFromContainer(null);
    setShowCropDeleteConfirmationModal(false);
  };

  // ---------------- SENSOR THRESHOLD VIEW FUNCTIONS ----------------
  // Fetch sensors with thresholds
  const fetchSensorsWithThresholds = async () => {
    try {
      const sensors = await getAllSensorsByContainerId(containerData._id);
      setSensorsWithThresholdsInContainer(sensors);
    } catch (error) {
      console.error("Error fetching sensors with thresholds: ", error);
    }
  };

  // Update sensor thresholds
  useEffect(() => {
    if (containerData && containerData._id) {
      fetchSensorsWithThresholds();
    }
  }, [containerData]);

  // Handle save button click for updating sensor thresholds
  const handleThresholdSaveClick = async (
    minThreshold,
    maxThreshold,
    isDelete = false,
  ) => {
    const containerId = containerData._id;
    const userId = localStorage.getItem("userId");

    let thresholds;

    if (isDelete) {
      // If deleting, set thresholds to null
      thresholds = {
        minThreshold: null,
        maxThreshold: null,
        userId: userId,
      };
    } else {
      // Normal saving logic
      minThreshold = parseFloat(minThreshold);
      maxThreshold = parseFloat(maxThreshold);

      thresholds = {
        minThreshold,
        maxThreshold,
        userId: userId,
      };

      if (isNaN(minThreshold) || isNaN(maxThreshold)) {
        toast.error(t("thresholds_must_be_numbers"));
        return;
      }

      if (minThreshold % 1 !== 0 || maxThreshold % 1 !== 0) {
        toast.error(t("thresholds_must_be_whole_numbers"));
      }

      if (minThreshold >= maxThreshold) {
        toast.error(t("min_threshold_must_be_less_than_max_threshold"));
        return;
      }

      if (
        minThreshold < sensorMinThreshold ||
        minThreshold > sensorMaxThreshold ||
        maxThreshold < sensorMinThreshold ||
        maxThreshold > sensorMaxThreshold
      ) {
        toast.error(
          `${t("threshold_values_must_be_between")}: ${sensorMinThreshold} - ${sensorMaxThreshold}`,
        );
        return;
      }
    }

    try {
      const response = await updateEverySensorThresholdsByContainerId(
        containerId,
        thresholds,
      );

      if (response) {
        fetchSensorsWithThresholds();
        toast.success(
          isDelete
            ? t("threshold_values_deleted_successfully")
            : t("threshold_values_updated_successfully"),
        );
      }
    } catch (error) {
      console.error("Error updating thresholds: ", error);
      toast.error("threshold_values_update_error");
    }
  };

  // Handle save button click for updating sensor thresholds with additive values
  const handleThresholdAdditiveSaveClick = async (
    minAdditive,
    maxAdditive,
    isDelete = false,
  ) => {
    const containerId = containerData._id;
    const userId = localStorage.getItem("userId");

    let additives;

    if (isDelete) {
      additives = {
        minAdditive: null,
        maxAdditive: null,
        userId: userId,
      };
    } else {
      minAdditive = parseFloat(minAdditive);
      maxAdditive = parseFloat(maxAdditive);

      additives = {
        minAdditive,
        maxAdditive,
        userId: userId,
      };

      if (isNaN(minAdditive) || isNaN(maxAdditive)) {
        toast.error(t("additive_values_must_be_numbers"));
        return;
      }

      if (minAdditive % 1 !== 0 || maxAdditive % 1 !== 0) {
        toast.error(t("additive_values_must_be_whole_numbers"));
      }

      if (minAdditive > maxAdditive) {
        toast.error(t("min_additive_must_be_less_than_max_additive"));
        return;
      }
    }

    try {
      const response = await updateEverySensorThresholdsAdditiveByContainerId(
        containerId,
        additives,
      );

      if (response) {
        fetchSensorsWithThresholds();
        toast.success(
          isDelete
            ? t("threshold_values_deleted_successfully")
            : t("additive_values_updated_successfully"),
        );
      }

      // Check if response.details exists and is an array (only for non-deletes)
      if (
        !isDelete &&
        response.details &&
        Array.isArray(response.details) &&
        response.details.length > 0
      ) {
        for (const detail of response.details) {
          toast.warning(
            `${t("threshold_values_update_error_exact")} ${detail.probeId} - ${t("threshold_values_update_error_exact_depth")} ${detail.sens_num}`,
          );
        }
      }
    } catch (error) {
      console.error("Error updating additives: ", error);
      fetchSensorsWithThresholds();
      toast.error(t("additive_values_update_error"));
    }
  };

  // ── 2. Handler: single sensor ─────────────────────────────────────────────────
  // Add this inside ContainerMoreInfoPage, alongside the existing threshold handlers.

  const handleSingleSensorThresholdSave = async (
    sensor,
    val1,
    val2,
    mode,
    isDelete = false,
  ) => {
    const userId = localStorage.getItem("userId");
    const sensorId = sensor.sensor_id;

    if (isDelete) {
      try {
        await updateSensorThresholdsBySensorIdInWarnings(sensorId, {
          minThreshold: null,
          maxThreshold: null,
          userId,
        });
        fetchSensorsWithThresholds();
        toast.success(t("threshold_values_deleted_successfully"));
      } catch (error) {
        console.error("Error deleting single sensor threshold:", error);
        toast.error(t("threshold_values_update_error"));
      }
      return;
    }

    if (isNaN(val1) || isNaN(val2)) {
      toast.error(t("thresholds_must_be_numbers"));
      return;
    }

    try {
      if (mode === "additive") {
        const currentValue = sensor.sens_value;
        const newMin = Math.round(currentValue + val1);
        const newMax = Math.round(currentValue + val2);

        if (newMin >= newMax) {
          toast.error(t("min_threshold_must_be_less_than_max_threshold"));
          return;
        }

        await updateSensorThresholdsBySensorIdInWarnings(sensorId, {
          minThreshold: newMin,
          maxThreshold: newMax,
          userId,
        });
      } else {
        const minThreshold = parseFloat(val1);
        const maxThreshold = parseFloat(val2);

        if (minThreshold >= maxThreshold) {
          toast.error(t("min_threshold_must_be_less_than_max_threshold"));
          return;
        }

        await updateSensorThresholdsBySensorIdInWarnings(sensorId, {
          minThreshold,
          maxThreshold,
          userId,
        });
      }

      fetchSensorsWithThresholds();
      toast.success(t("threshold_values_updated_successfully"));
    } catch (error) {
      console.error("Error updating single sensor threshold:", error);
      toast.error(t("threshold_values_update_error"));
    }
  };

  const handleProbeLevelSave = async (
    probeid,
    sensors,
    val1,
    val2,
    mode,
    isDelete = false,
  ) => {
    const userId = localStorage.getItem("userId");

    if (isDelete) {
      try {
        const promises = sensors.map((sensor) =>
          updateSensorThresholdsBySensorIdInWarnings(sensor.sensor_id, {
            minThreshold: null,
            maxThreshold: null,
            userId,
          }),
        );
        await Promise.allSettled(promises);
        fetchSensorsWithThresholds();
        toast.success(t("threshold_values_deleted_successfully"));
      } catch (error) {
        console.error("Error deleting probe thresholds:", error);
        toast.error(t("threshold_values_update_error"));
      }
      return;
    }

    if (isNaN(val1) || isNaN(val2)) {
      toast.error(t("thresholds_must_be_numbers"));
      return;
    }

    try {
      if (mode === "additive") {
        const promises = sensors.map(async (sensor) => {
          const currentValue = sensor.sens_value;
          const newMin = Math.round(currentValue + val1);
          const newMax = Math.round(currentValue + val2);

          if (
            newMin >= newMax ||
            newMin < sensorMinThreshold ||
            newMax > sensorMaxThreshold
          ) {
            toast.warning(
              `${t("threshold_values_update_error_exact")} ${sensor.probeid} – ${t("depth")} ${sensor.sens_num}`,
            );
            return;
          }

          return updateSensorThresholdsBySensorIdInWarnings(sensor.sensor_id, {
            minThreshold: newMin,
            maxThreshold: newMax,
            userId,
          });
        });

        await Promise.allSettled(promises);
      } else {
        const minThreshold = parseFloat(val1);
        const maxThreshold = parseFloat(val2);

        if (minThreshold >= maxThreshold) {
          toast.error(t("min_threshold_must_be_less_than_max_threshold"));
          return;
        }

        const promises = sensors.map((sensor) =>
          updateSensorThresholdsBySensorIdInWarnings(sensor.sensor_id, {
            minThreshold,
            maxThreshold,
            userId,
          }),
        );

        await Promise.allSettled(promises);
      }

      fetchSensorsWithThresholds();
      toast.success(t("threshold_values_updated_successfully"));
    } catch (error) {
      console.error("Error updating probe thresholds:", error);
      toast.error(t("threshold_values_update_error"));
    }
  };

  // ---------------- GRAPH VIEW FUNCTIONS ----------------
  // Fetch sensors with no measurement data for the graph view
  const fetchSensorsWithNoMesDataInContainer = async () => {
    try {
      const sensors = await getAllSensorsWithNoMesDataByContainerId(
        containerData._id,
      );
      setSensorsForSelectionInGraphView(sensors);
    } catch (error) {
      console.error("Error fetching sensors with no mes data: ", error);
    }
  };

  useEffect(() => {
    if (containerData && containerData._id) {
      fetchSensorsWithNoMesDataInContainer();
    }
  }, [containerData]);

  return (
    <div className="container locations-page__container navbar-margin">
      {/* Include the unauthorized access modal - will only show when needed */}
      {UnauthorizedModal}

      {/* Show loading animation only when actively loading and no data yet */}
      {isLoadingContainer && !containerData && <LoadingAnimation />}

      {/* Show any non-auth errors */}
      {containerError && (
        <div className="alert alert-danger">{containerError}</div>
      )}

      {/* Only render content when we have container data */}
      {containerData && (
        <>

          {
            <PageHeader
              /*title={`${siteName} - ${containerData.containername}`} */
              title={pageTitle}
              helperTitle={containerData.containername}
              helperBody={t("container_more_info_page_helper")}
            />
          }
          {/* MODIFIED PAGE HEADER COMES HERE, ONLY USED HERE ELSE PAGEHEADER IS USED */}
          <div className="cmip__header-filter__container">
            {/* Dropdown for company selection in case of superadmin user */}
            {user.roles === USER_ROLES.SUPER_ADMIN && (
              <div className="locations-page__company-dropdown">
                <FormSelect
                  className="locations-page__company-dropdown-select"
                  value={selectedCompanyForSuperAdmin || ""}
                  onChange={(e) => handleCompanySelectChange(e.target.value)}
                >
                  <option value="">{t("selectCompany")}</option>
                  {listOfCompaniesForSuperAdmin.map((company) => (
                    <option key={company.companyid} value={company.companyid}>
                      {company.companyname}
                    </option>
                  ))}
                </FormSelect>
              </div>
            )}
            {/* Dropdowns for both superadmin and users */}
            {/* Dropdown for site selection */}
            {listOfSitesInTheCompany.length > 0 && (
              <div className="locations-page__site-dropdown">
                <FormSelect
                  className="locations-page__site-dropdown-select"
                  value={selectedSiteFromList || ""}
                  onChange={(e) => handleSiteSelectChange(e.target.value)}
                >
                  <option value="">{t("selectSite")}</option>
                  {listOfSitesInTheCompany.map((site) => (
                    <option key={site._id} value={site._id}>
                      {site.sitename}
                    </option>
                  ))}
                </FormSelect>
              </div>
            )}
            {/* Dropdown for container selection */}
            {selectedSiteFromList !== null &&
              (listOfContainersInTheSite.length > 0 ? (
                <div className="locations-page__container-dropdown">
                  <FormSelect
                    className="locations-page__container-dropdown-select"
                    value={selectedContainerFromList || ""}
                    onChange={(e) =>
                      handleContainerSelectChange(e.target.value)
                    }
                  >
                    <option value="">{t("selectContainer")}</option>
                    {listOfContainersInTheSite.map((container) => (
                      <option key={container._id} value={container._id}>
                        {container.containername}
                      </option>
                    ))}
                  </FormSelect>
                </div>
              ) : (
                <p>{t("noContainersInSite")}</p>
              ))}
          </div>
          <div className="container-moreinfo-page__view-buttons__container">
            <ButtonGroup
              aria-label="Basic example"
              size="sm"
              className="container-view-button-group"
            >
              <Button
                variant="secondary"
                className={`container-view-button ${
                  view === "map" ? "active" : ""
                }`}
                onClick={() => setView("map")}
                active={view === "map"}
              >
                {t("container")}
              </Button>
              <Button
                variant="secondary"
                className={`container-view-button ${
                  view === "3d" ? "active" : ""
                }`}
                onClick={() => setView("3d")}
                active={view === "3d"}
              >
                {t("3d_view")}
              </Button>
              <Button
                variant="secondary"
                className={`container-view-button ${
                  view === "sensor" ? "active" : ""
                }`}
                onClick={() => setView("sensor")}
                active={view === "sensor"}
              >
                {t("sensor")}
              </Button>
              <Button
                variant="secondary"
                className={`container-view-button ${
                  view === "graph" ? "active" : ""
                }`}
                onClick={() => setView("graph")}
                active={view === "graph"}
              >
                {t("graph")}
              </Button>
              <Button
                variant="secondary"
                className={`container-view-button ${
                  view === "crop" ? "active" : ""
                }`}
                onClick={() => setView("crop")}
                active={view === "crop"}
              >
                {t("crop")}
              </Button>
            </ButtonGroup>
          </div>

          {view === "map" && (
            <ContainerFloorPlanView
              devices={devices}
              handleDeviceMove={handleDeviceMove}
              imageData={imageData}
              imageRef={imageRef}
              imageSize={imageSize}
              setImageSize={setImageSize}
              showImageUpload={showImageUpload}
              setShowImageUpload={setShowImageUpload}
              containerId={containerData._id}
            />
          )}

          {view === "sensor" && (
            <ContainerThresholdsView
              containerId={containerData._id}
              sensorsTableHeader={thresholdViewTableHeader}
              sensorsData={sensorsWithThresholdsInContainer}
              handleThresholdSaveClick={handleThresholdSaveClick}
              handleThresholdAdditiveSaveClick={
                handleThresholdAdditiveSaveClick
              }
              handleSingleSensorThresholdSave={handleSingleSensorThresholdSave} // NEW
              handleProbeLevelSave={handleProbeLevelSave} // NEW
              sensorMinThreshold={sensorMinThreshold}
              sensorMaxThreshold={sensorMaxThreshold}
            />
          )}
          {view === "graph" && (
            <ContainerGraphView
              containerData={containerData} // Pass container data
              devices={devices}
              sensorsForSelectionInGraphView={sensorsForSelectionInGraphView}
              isLoadingGraphData={isLoadingGraphData}
              setIsLoadingGraphData={setIsLoadingGraphData}
            />
          )}
          {view === "3d" && (
            <ContainerThreeDView containerId={containerData._id} t={t} />
          )}
          {view === "crop" && (
            <ContainerCropView
              containerId={containerData._id}
              container={containerData}
              cropData={crops.data}
              selectedCompanyForSuperAdmin={selectedCompanyForSuperAdmin}
              listOfCropsInCompany={listOfCropsInCompany}
              showConnectCropToContainerModal={showConnectCropToContainerModal}
              handleSubmitConnectCropToContainerButtonClick={
                handleSubmitConnectCropToContainerButtonClick
              }
              handleShowConnectCropToContainerModal={
                handleShowConnectCropToContainerModal
              }
              handleCloseConnectCropToContainerModal={
                handleCloseConnectCropToContainerModal
              }
              handleAddNewCropButtonClick={handleAddNewCropButtonClick}
              selectedCropToConnectToContainer={
                selectedCropToConnectToContainer
              }
              setSelectedCropToConnectToContainer={
                setSelectedCropToConnectToContainer
              }
              handleShowCropDeleteConfirmationModal={
                handleShowCropDeleteConfirmationModal
              }
              selectedCropToDeleteFromContainer={
                selectedCropToDeleteFromContainer
              }
              setSelectedCropToDeleteFromContainer={
                setSelectedCropToDeleteFromContainer
              }
            />
          )}
          {showCropDeleteConfirmationModal && (
            <ConfirmationModal
              isVisible={showCropDeleteConfirmationModal}
              onConfirm={handleDeleteCropFromContainerConfirm}
              onCancel={handleDeleteCropFromContainerCancel}
              message={`Biztosan törli a terményt a tárolóból?`}
            />
          )}
          {/* Render other components based on containerData */}
        </>
      )}
    </div>
  );
};
export default ContainerMoreInfoPage;
